import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { AuditLiveMentionConfidence } from '@mi-front/core';
import { useCallback, useMemo, useState } from 'react';
import { useVerifyMatchDialog } from './useVerifyMatchDialog';
import { VerifyMatchTarget } from './verify-match-types';
import { VerifyMatchDialogProps } from './VerifyMatchDialog';

export interface UseVerifyMatchListDialogResult<TRow> {
  /** Opens the verify dialog at a specific list target. */
  openAt: (target: VerifyMatchTarget) => void;
  /** Pass to MrtTable so dialog navigation follows visible row order. */
  onDisplayRowsChange: (rows: TRow[]) => void;
  /**
   * Props for `VerifyMatchListDialog`, or null until a target has been opened at least once
   * (keeps MUI exit transition working with the last target).
   */
  dialogProps: VerifyMatchDialogProps | null;
}

/**
 * Owns display-row tracking and dialog open/navigation state for a list screen.
 * Display rows are filled by `onDisplayRowsChange` from MrtTable (no stale initial seed).
 */
export function useVerifyMatchListDialog<TRow>(
  rowToTarget: (row: TRow) => VerifyMatchTarget | null,
  onRefresh?: () => void | Promise<void>,
): UseVerifyMatchListDialogResult<TRow> {
  const station = useActiveStation();
  const [displayRows, setDisplayRows] = useState<TRow[]>([]);

  const targets = useMemo<VerifyMatchTarget[]>(
    () =>
      displayRows.flatMap((row) => {
        const target = rowToTarget(row);
        return target ? [target] : [];
      }),
    [displayRows, rowToTarget],
  );

  const { open, target, openAt, close, navigation } = useVerifyMatchDialog(targets);

  const onMentionConfidenceChange = useCallback(
    (_mentionId: string, _confidence: AuditLiveMentionConfidence) => {
      void onRefresh?.();
    },
    [onRefresh],
  );

  const dialogProps: VerifyMatchDialogProps | null = target
    ? {
        open,
        onClose: close,
        station,
        target,
        navigation,
        onMentionConfidenceChange: onRefresh ? onMentionConfidenceChange : undefined,
      }
    : null;

  return {
    openAt,
    onDisplayRowsChange: setDisplayRows,
    dialogProps,
  };
}
